//rectangleBlur.js

const blurVertexShader = /* glsl */ `
varying vec2 v_texcoord;
void main() {
    v_texcoord = uv;
    // Keeping projectionMatrix prevents frustum clipping in the global scene
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const blurFragmentShader = /* glsl */ `
varying vec2 v_texcoord;

uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform vec3 u_bgColor;
uniform vec3 u_borderColor;
uniform float u_bgOpacity;
uniform float u_shapeSize;
uniform float u_roundness;
uniform float u_borderSize;
uniform float u_circleSize;
uniform float u_circleEdge;
uniform float u_tailSide;
uniform float u_padding;
uniform float u_enableBlur;

float dot2( in vec2 v ) { return dot(v,v); }

// Smooth Minimum (creates organic, liquid-like merging between shapes)
float smin(float a, float b, float k) {
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
}

float sdRoundRect(vec2 p, vec2 b, float r) {
    vec2 d = abs(p) - b + vec2(r);
    return min(max(d.x, d.y), 0.0) + length(max(d, 0.0)) - r;
}

float sdTail(vec2 p, float size) {
    // 45-degree rotation
    float c = 0.70710678; 
    vec2 rotP = vec2(p.x * c - p.y * c, p.x * c + p.y * c);
    
    // Standard square diamond
    vec2 d = abs(rotP) - vec2(size);
    return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}

void main() {
    vec2 st = v_texcoord * u_resolution.xy;
    vec2 p = st - u_resolution.xy * 0.5; 

    vec2 b = (u_resolution.xy - 2.0 * u_padding) * 0.5;
    b *= u_shapeSize;

    float boxDist = sdRoundRect(p, b, u_roundness);
    
    float tailDist = 9999.0;
    float tailSize = 22.0; 
    
    if (u_tailSide < -0.5) { 
        // Shift tail inward so it anchors deeply into the box
        vec2 tailPos = p - vec2(-b.x + tailSize * 0.5, 0.0);
        tailDist = sdTail(tailPos, tailSize);
    } else if (u_tailSide > 0.5) { 
        vec2 tailPos = p - vec2(b.x - tailSize * 0.5, 0.0);
        tailDist = sdTail(tailPos, tailSize);
    }

    // smin() perfectly melts the tail into the main bubble!
    float sdf = smin(boxDist, tailDist, 18.0);

    vec2 posMouse = u_mouse;
    posMouse.y *= -1.0; 
    
    float distToMouse = length(p - posMouse);
    
    float interactionAlpha = (1.0 - smoothstep(u_circleSize - u_circleSize * u_circleEdge, u_circleSize + u_circleSize * u_circleEdge, distToMouse)) * u_enableBlur;
    
    float maxBlur = 40.0; 
    float currentBlur = 1.0 + (interactionAlpha * maxBlur);
    float halfW = u_borderSize * 0.5;
    float smoothEdge = max(currentBlur, 1.0);
    
    // 1. Calculate the pure border (glow)
    float borderAlpha = smoothstep(-halfW - smoothEdge, -halfW + smoothEdge, sdf) 
                      - smoothstep(halfW - smoothEdge, halfW + smoothEdge, sdf);
    
    borderAlpha = clamp(borderAlpha * (1.0 + interactionAlpha * 1.8), 0.0, 1.0);
    
    // 2. Calculate the pure background shape
    float bgMask = 1.0 - smoothstep(-1.0, 1.0, sdf);
    float bgAlpha = bgMask * u_bgOpacity;

    // 3. Composite layers mathematically (Top Layer: Border, Bottom Layer: BG)
    float finalAlpha = borderAlpha + bgAlpha * (1.0 - borderAlpha);
    
    vec3 finalColor = vec3(0.0);
    
    // Prevent divide-by-zero, then mix based on alpha weight so the glow stays pure white!
    if (finalAlpha > 0.0001) {
        finalColor = mix(u_bgColor, u_borderColor, borderAlpha / finalAlpha);
    }

    gl_FragColor = vec4(finalColor, finalAlpha);
}
`;

export class ShapeBlur {
    constructor(container, block, settings, dependencies) {
        this.container = container;
        this.settings = {
            shapeSize: 1.0,
            roundness: 20.0,
            borderSize: 2.0,
            circleSize: 100.0,
            circleEdge: 0.6,
            bgOpacity: 1.0,
            bgColor: '#000000',
            borderColor: '#ffffff',
            ...settings
        };
        
        // I noticed you passed THREE and webGlManager separately in your Heart snippet, 
        // but here we unwrap it from the dependencies object mapped in your Liquid file!
        this.THREE = dependencies.THREE;
        this.webGlManager = dependencies.webGlManager;

        let tailSide = 0.0;
        if (this.settings.tailPosition === 'left') tailSide = -1.0;
        if (this.settings.tailPosition === 'right') tailSide = 1.0;

        this.isMobile = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
        this.qualityTier = 4;
        
        this.vMouse = new this.THREE.Vector2(-2000, -2000);
        this.vMouseDamp = new this.THREE.Vector2(-2000, -2000);
        this.lastTime = performance.now() * 0.001;

        let bgColorObj, borderColorObj;
        try {
            bgColorObj = new this.THREE.Color(this.settings.bgColor);
            borderColorObj = new this.THREE.Color(this.settings.borderColor);
        } catch(e) {
            bgColorObj = new this.THREE.Color('#000000'); 
            borderColorObj = new this.THREE.Color('#ffffff');
        }

        const initialEnableBlur = (!this.isMobile && this.qualityTier >= 4) ? 1.0 : 0.0;

        this.material = new this.THREE.ShaderMaterial({
            vertexShader: blurVertexShader,
            fragmentShader: blurFragmentShader,
            uniforms: {
                u_resolution: { value: new this.THREE.Vector2() },
                u_mouse: { value: this.vMouseDamp },
                u_bgColor: { value: bgColorObj },
                u_borderColor: { value: borderColorObj },
                u_bgOpacity: { value: this.settings.bgOpacity },
                u_shapeSize: { value: this.settings.shapeSize },
                u_roundness: { value: this.settings.roundness },
                u_borderSize: { value: this.settings.borderSize },
                u_circleSize: { value: this.settings.circleSize },
                u_circleEdge: { value: this.settings.circleEdge },
                u_tailSide: { value: tailSide },
                u_padding: { value: 100.0 }, 
                u_enableBlur: { value: initialEnableBlur }
            },
            transparent: true,
            blending: this.THREE.NormalBlending,
            depthTest: false,
            depthWrite: false
        });

        this.onPointerMove = this.onPointerMove.bind(this);

        if (!this.isMobile) {
            document.addEventListener('pointermove', this.onPointerMove, { passive: true });
        }

        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.container);
        
        if (this.webGlManager) {
            this.webGlManager.register(this, 10);
        }

        this.resize();
    }

    onResize() {
        this.resize();
    }

    setQuality(newTier) {
        if (this.qualityTier !== newTier) {
            this.qualityTier = newTier;
            const enableBlur = (!this.isMobile && this.qualityTier >= 4) ? 1.0 : 0.0;
            this.material.uniforms.u_enableBlur.value = enableBlur;
        }
    }

    onPointerMove(e) {
        const rect = this.container.getBoundingClientRect();
        if (rect.width === 0) return; 
        
        const dpr = this.webGlManager ? this.webGlManager.renderer.getPixelRatio() : 1;
        
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        this.vMouse.set((e.clientX - centerX) * dpr, (e.clientY - centerY) * dpr);
    }

    resize() {
        const rect = this.container.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;

        const dpr = this.webGlManager && this.webGlManager.renderer ? this.webGlManager.renderer.getPixelRatio() : 1;
        
        this.material.uniforms.u_resolution.value.set(rect.width * dpr, rect.height * dpr);
        // Force the padding to scale perfectly across retina displays
        this.material.uniforms.u_padding.value = 100.0 * dpr; 
    }

    update(timeMillis) {
        const time = timeMillis * 0.001;
        let dt = time - this.lastTime;
        
        if (dt < 0 || dt > 0.1) dt = 0.016; 
        this.lastTime = time;

        if (this.material.uniforms.u_enableBlur.value > 0.5) {
            this.vMouseDamp.x = this.THREE.MathUtils.damp(this.vMouseDamp.x, this.vMouse.x, 12, dt);
            this.vMouseDamp.y = this.THREE.MathUtils.damp(this.vMouseDamp.y, this.vMouse.y, 12, dt);
            
            if(this.material.uniforms.u_mouse) {
                this.material.uniforms.u_mouse.value.copy(this.vMouseDamp);
            }
        }
    }

    destroy() {
        if (this.webGlManager) this.webGlManager.unregister(this);
        if (!this.isMobile) document.removeEventListener('pointermove', this.onPointerMove);
        this.resizeObserver.disconnect();
        this.material.dispose();
    }
}

window.ShapeBlur = ShapeBlur;